import React, { useEffect, useState, useRef } from 'react';
import {
    Button,
    Card,
    Table,
    Modal,
    Form,
    Input,
    TimePicker,
    Switch,
    Space,
    message,
    Popconfirm,
    Tag,
    Descriptions,
    Tour,
    ConfigProvider,
    Row,
    Col,
    Select,
    InputNumber,
    Checkbox,
    Divider,
} from 'antd';
import type { TableColumnsType, TourProps } from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ClockCircleOutlined,
    QuestionCircleOutlined,
    EyeOutlined,
    MenuOutlined,
    DownOutlined,
    UpOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import viVN from 'antd/locale/vi_VN';

import AdminLayout from '../components/AdminLayout';
import { storeService } from '../services/storeService';
import { storeMenuService } from '../services/storeMenuService';
import type { Store, StoreCreateInput, StoreUpdateInput, StoreMenu, StoreMenuCreateInput, Product, MenuItem } from '../types/store';

const { TextArea } = Input;

const StoreManagement: React.FC = () => {
    const [stores, setStores] = useState<Store[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [editingStore, setEditingStore] = useState<Store | null>(null);
    const [viewingStore, setViewingStore] = useState<Store | null>(null);
    const [form] = Form.useForm();
    const [isTourOpen, setIsTourOpen] = useState(false);

    // Loading states for different actions
    const [submitLoading, setSubmitLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
    const [toggleLoading, setToggleLoading] = useState<string | null>(null);

    // Menu management states
    const [expandedStoreId, setExpandedStoreId] = useState<string | null>(null);
    const [storeMenus, setStoreMenus] = useState<{ [storeId: string]: StoreMenu[] }>({});
    const [menuModalVisible, setMenuModalVisible] = useState(false);
    const [editingMenu, setEditingMenu] = useState<StoreMenu | null>(null);
    const [currentStoreId, setCurrentStoreId] = useState<string>('');
    const [menuForm] = Form.useForm();
    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [loadingMenus, setLoadingMenus] = useState(false);

    // Menu loading states
    const [menuSubmitLoading, setMenuSubmitLoading] = useState(false);
    const [menuDeleteLoading, setMenuDeleteLoading] = useState<string | null>(null);
    const [menuToggleLoading, setMenuToggleLoading] = useState<string | null>(null);

    // Tour refs
    const titleRef = useRef(null);
    const addButtonRef = useRef(null);
    const tableRef = useRef(null);

    // Tour steps
    const tourSteps: TourProps['steps'] = [
        {
            title: 'Quản lý cửa hàng',
            description: 'Chào mừng bạn đến với trang Quản lý cửa hàng! Đây là nơi bạn có thể quản lý tất cả thông tin về các cửa hàng, bao gồm địa chỉ, giờ làm việc và trạng thái hoạt động.',
            target: () => titleRef.current,
        },
        {
            title: 'Thêm cửa hàng mới',
            description: 'Nhấn vào nút này để thêm cửa hàng mới. Bạn có thể nhập đầy đủ thông tin bao gồm địa chỉ và giờ làm việc cho từng ngày trong tuần.',
            target: () => addButtonRef.current,
        },
        {
            title: 'Danh sách cửa hàng',
            description: 'Bảng này hiển thị tất cả các cửa hàng hiện có. Bạn có thể xem chi tiết, chỉnh sửa, xóa, quản lý menu hoặc thay đổi trạng thái hoạt động của từng cửa hàng.',
            target: () => tableRef.current,
        },
    ];

    useEffect(() => {
        fetchStores();
        fetchAllProducts();
    }, []);

    const fetchStores = async () => {
        setLoading(true);
        try {
            const data = await storeService.getStores();
            setStores(data);
        } catch (error) {
            console.error('Error fetching stores:', error);
            message.error('Không thể tải danh sách cửa hàng');
        }
        setLoading(false);
    };

    const fetchAllProducts = async () => {
        try {
            const products = await storeMenuService.getAllProducts();

            // Group sản phẩm theo originalId để chỉ hiển thị một đại diện cho mỗi loại
            const groupedProducts = new Map<string, Product>();

            products.forEach(product => {
                // Sử dụng originalId làm key để group (ưu tiên originalId hơn originalName)
                const groupKey = product.originalId || product.id;

                // Nếu chưa có sản phẩm nào trong group này
                if (!groupedProducts.has(groupKey)) {
                    // Tạo product đại diện với tên gốc (không có weight/volume)
                    const representativeProduct = {
                        ...product,
                        // Đảm bảo hiển thị tên gốc, không có suffix như "- 100g" hoặc "- 500ml"
                        name: product.originalName || product.name
                    };
                    groupedProducts.set(groupKey, representativeProduct);
                }
                // Không cần so sánh weight nữa vì chúng ta chỉ cần một đại diện
            });

            // Chuyển Map thành array và sort
            const uniqueProducts = Array.from(groupedProducts.values()).sort((a, b) => {
                // Sort theo type trước, sau đó theo tên
                if (a.type !== b.type) {
                    const typeOrder = { 'coffee': 1, 'bottledDrink': 2, 'dish': 3 };
                    return typeOrder[a.type] - typeOrder[b.type];
                }
                const nameA = a.originalName || a.name || '';
                const nameB = b.originalName || b.name || '';
                return nameA.localeCompare(nameB, 'vi');
            });

            setAllProducts(uniqueProducts);
        } catch (error) {
            console.error('Error fetching products:', error);
            message.error('Không thể tải danh sách sản phẩm');
        }
    };

    const fetchStoreMenus = async (storeId: string) => {
        setLoadingMenus(true);
        try {
            const menus = await storeMenuService.getMenusByStoreId(storeId);
            setStoreMenus(prev => ({
                ...prev,
                [storeId]: menus
            }));
        } catch (error) {
            console.error('Error fetching store menus:', error);

            // Hiển thị thông báo chi tiết nếu là lỗi index
            if ((error as any)?.message?.includes('index') || (error as any)?.message?.includes('requires an index')) {
                message.warning('Hệ thống đang thiết lập index cho database. Menu sẽ hiển thị sau vài phút.', 5);
            }

            // Set empty array để hiển thị trạng thái trống
            setStoreMenus(prev => ({
                ...prev,
                [storeId]: []
            }));
        }
        setLoadingMenus(false);
    };

    const handleExpandStore = async (storeId: string) => {
        if (expandedStoreId === storeId) {
            setExpandedStoreId(null);
        } else {
            setExpandedStoreId(storeId);
            if (!storeMenus[storeId]) {
                await fetchStoreMenus(storeId);
            }
        }
    };

    const handleAdd = () => {
        setEditingStore(null);
        form.resetFields();
        form.setFieldsValue({
            workingHours: {
                weekdays: {
                    open: dayjs('09:00', 'HH:mm'),
                    close: dayjs('18:00', 'HH:mm'),
                    isOpen: true,
                },
                weekends: {
                    open: dayjs('09:00', 'HH:mm'),
                    close: dayjs('18:00', 'HH:mm'),
                    isOpen: true,
                },
            },
            isActive: true,
        });
        setModalVisible(true);
    };

    const handleEdit = (record: Store) => {
        setEditingStore(record);
        form.setFieldsValue({
            ...record,
            workingHours: {
                weekdays: {
                    open: record.workingHours.weekdays.open ? dayjs(record.workingHours.weekdays.open, 'HH:mm') : null,
                    close: record.workingHours.weekdays.close ? dayjs(record.workingHours.weekdays.close, 'HH:mm') : null,
                    isOpen: record.workingHours.weekdays.isOpen,
                },
                weekends: {
                    open: record.workingHours.weekends.open ? dayjs(record.workingHours.weekends.open, 'HH:mm') : null,
                    close: record.workingHours.weekends.close ? dayjs(record.workingHours.weekends.close, 'HH:mm') : null,
                    isOpen: record.workingHours.weekends.isOpen,
                },
            },
        });
        setModalVisible(true);
    };

    const handleView = (record: Store) => {
        setViewingStore(record);
        setDetailModalVisible(true);
    };

    const handleDelete = async (id: string) => {
        setDeleteLoading(id);
        try {
            await storeService.deleteStore(id);
            message.success('Đã xóa cửa hàng thành công!');
            fetchStores();
        } catch (error) {
            console.error('Error deleting store:', error);
            message.error('Không thể xóa cửa hàng. Vui lòng thử lại!');
        } finally {
            setDeleteLoading(null);
        }
    };

    const handleToggleStatus = async (id: string, isActive: boolean) => {
        setToggleLoading(id);
        try {
            await storeService.toggleStoreStatus(id, isActive);
            message.success(`Đã ${isActive ? 'kích hoạt' : 'tạm dừng'} cửa hàng thành công!`);
            fetchStores();
        } catch (error) {
            console.error('Error toggling store status:', error);
            message.error('Không thể thay đổi trạng thái cửa hàng. Vui lòng thử lại!');
        } finally {
            setToggleLoading(null);
        }
    };

    const handleSubmit = async (values: any) => {
        setSubmitLoading(true);
        try {
            const formattedValues: StoreCreateInput | StoreUpdateInput = {
                ...values,
                workingHours: {
                    weekdays: {
                        open: values.workingHours.weekdays.open?.format('HH:mm') || '',
                        close: values.workingHours.weekdays.close?.format('HH:mm') || '',
                        isOpen: values.workingHours.weekdays.isOpen || false,
                    },
                    weekends: {
                        open: values.workingHours.weekends.open?.format('HH:mm') || '',
                        close: values.workingHours.weekends.close?.format('HH:mm') || '',
                        isOpen: values.workingHours.weekends.isOpen || false,
                    },
                },
            };

            if (editingStore) {
                await storeService.updateStore({
                    ...formattedValues,
                    id: editingStore.id!,
                } as StoreUpdateInput);
                message.success('Đã cập nhật thông tin cửa hàng thành công!');
            } else {
                await storeService.createStore(formattedValues as StoreCreateInput);
                message.success('Đã tạo cửa hàng mới thành công!');
            }

            setModalVisible(false);
            form.resetFields();
            fetchStores();
        } catch (error) {
            console.error('Error saving store:', error);
            message.error(editingStore ? 'Không thể cập nhật cửa hàng. Vui lòng thử lại!' : 'Không thể tạo cửa hàng mới. Vui lòng thử lại!');
        } finally {
            setSubmitLoading(false);
        }
    };

    // Menu management functions
    const handleAddMenu = (storeId: string, storeName: string) => {
        setCurrentStoreId(storeId);
        setEditingMenu(null);
        menuForm.resetFields();
        menuForm.setFieldsValue({
            menuName: `Menu ${storeName}`,
            isActive: true,
            selectedProducts: []
        });
        setMenuModalVisible(true);
    };

    const handleEditMenu = (menu: StoreMenu) => {
        setCurrentStoreId(menu.storeId);
        setEditingMenu(menu);

        // Lấy danh sách uniqueId từ items để set vào selectedProducts
        // Lấy danh sách productId từ items để set vào selectedProducts
        const selectedProductIds = (menu.items || []).map(item => {
            // Tìm product trong allProducts dựa trên originalId hoặc productId
            const matchingProduct = allProducts.find(p =>
                p.originalId === item.productId ||
                p.id === item.productId ||
                (p.originalName && p.originalName === item.originalName)
            );
            return matchingProduct ? matchingProduct.id : item.productId;
        }).filter(id => id); // Lọc bỏ các ID null/undefined

        menuForm.setFieldsValue({
            menuName: menu.menuName,
            isActive: menu.isActive,
            selectedProducts: selectedProductIds
        });
        setMenuModalVisible(true);
    };

    const handleDeleteMenu = async (menuId: string, storeId: string) => {
        setMenuDeleteLoading(menuId);
        try {
            await storeMenuService.deleteMenu(menuId);
            message.success('Đã xóa menu thành công!');
            await fetchStoreMenus(storeId);
        } catch (error) {
            console.error('Error deleting menu:', error);
            message.error('Không thể xóa menu. Vui lòng thử lại!');
        } finally {
            setMenuDeleteLoading(null);
        }
    };

    const handleToggleMenuStatus = async (menuId: string, isActive: boolean, storeId: string) => {
        setMenuToggleLoading(menuId);
        try {
            await storeMenuService.toggleMenuStatus(menuId, isActive);
            message.success(`Đã ${isActive ? 'kích hoạt' : 'tạm dừng'} menu thành công!`);
            await fetchStoreMenus(storeId);
        } catch (error) {
            console.error('Error toggling menu status:', error);
            message.error('Không thể thay đổi trạng thái menu. Vui lòng thử lại!');
        } finally {
            setMenuToggleLoading(null);
        }
    };

    const handleMenuSubmit = async (values: any) => {
        setMenuSubmitLoading(true);
        try {
            // Kiểm tra dữ liệu đầu vào
            if (!values.selectedProducts || values.selectedProducts.length === 0) {
                message.error('Vui lòng chọn ít nhất một món cho menu');
                return;
            }

            if (!currentStoreId) {
                message.error('Không xác định được cửa hàng');
                return;
            }

            // Tạo items từ selectedProducts - chỉ lưu ID gốc của sản phẩm
            const cleanItems = (values.selectedProducts || []).map((productId: string) => {
                const product = allProducts.find(p => p.id === productId);

                if (!product) {
                    console.warn('Product not found for ID:', productId);
                    return null;
                }

                // Chỉ lưu thông tin cơ bản, sử dụng originalId hoặc originalName làm ID chính
                const cleanedItem: any = {
                    productId: product.originalId || product.id, // ID gốc của sản phẩm
                    productType: product.type,
                    name: product.originalName || product.name, // Tên gốc của sản phẩm
                    isAvailable: true // Mặc định là có sẵn
                };

                // Chỉ thêm originalId và originalName nếu có
                if (product.originalId) cleanedItem.originalId = product.originalId;
                if (product.originalName) cleanedItem.originalName = product.originalName;
                if (product.codeCoffee) cleanedItem.codeCoffee = product.codeCoffee;
                if (product.productCode) cleanedItem.productCode = product.productCode;
                if (product.code) cleanedItem.code = product.code;

                return cleanedItem;
            }).filter((item: any) => item !== null); // Loại bỏ items null

            const menuData: StoreMenuCreateInput = {
                storeId: currentStoreId,
                menuName: values.menuName,
                items: cleanItems,
                isActive: values.isActive ?? true
            };

            if (editingMenu) {
                await storeMenuService.updateMenu({
                    ...menuData,
                    id: editingMenu.id!
                });
                message.success('Đã cập nhật menu thành công!');
            } else {
                const newMenuId = await storeMenuService.createMenu(menuData);
                message.success('Đã tạo menu mới thành công!');
            }

            setMenuModalVisible(false);
            menuForm.resetFields();

            // Delay nhỏ để đảm bảo Firestore đã sync
            setTimeout(async () => {
                await fetchStoreMenus(currentStoreId);
            }, 1000);
        } catch (error) {
            console.error('Error saving menu:', error);
            message.error(editingMenu ? 'Không thể cập nhật menu. Vui lòng thử lại!' : 'Không thể tạo menu mới. Vui lòng thử lại!');
        } finally {
            setMenuSubmitLoading(false);
        }
    };

    const formatWorkingHours = (workingHours: any) => {
        const formatTime = (period: any) => {
            if (!period.isOpen) return 'Đóng cửa';
            return `${period.open} - ${period.close}`;
        };

        return (
            <div>
                <div><strong>Thứ 2-6:</strong> {formatTime(workingHours.weekdays)}</div>
                <div><strong>Thứ 7-CN:</strong> {formatTime(workingHours.weekends)}</div>
            </div>
        );
    };

    const renderMenuTable = (storeId: string) => {
        const menus = storeMenus[storeId] || [];

        const menuColumns: TableColumnsType<StoreMenu> = [
            {
                title: 'Tên menu',
                dataIndex: 'menuName',
                key: 'menuName',
                width: 200,
            },
            {
                title: 'Món ăn',
                key: 'items',
                width: 300,
                render: (_, record: StoreMenu) => (
                    <div>
                        <div style={{ marginBottom: 4 }}>
                            <strong>{record.items?.length || 0} món</strong>
                        </div>
                        {record.items?.slice(0, 3).map((item, idx) => (
                            <div key={idx} style={{ fontSize: '12px', color: '#666' }}>
                                • {item.name}
                            </div>
                        ))}
                        {record.items && record.items.length > 3 && (
                            <div style={{ fontSize: '12px', color: '#999' }}>
                                ...và {record.items.length - 3} món khác
                            </div>
                        )}
                    </div>
                ),
            },
            {
                title: 'Trạng thái',
                dataIndex: 'isActive',
                key: 'isActive',
                width: 120,
                render: (isActive: boolean, record: StoreMenu) => (
                    <Switch
                        checked={isActive}
                        loading={menuToggleLoading === record.id}
                        onChange={(checked) => handleToggleMenuStatus(record.id!, checked, storeId)}
                        checkedChildren="Hoạt động"
                        unCheckedChildren="Tạm dừng"
                        size="small"
                    />
                ),
            },
            {
                title: 'Thao tác',
                key: 'actions',
                width: 150,
                render: (_, record: StoreMenu) => (
                    <Space>
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => handleEditMenu(record)}
                            size="small"
                            title="Chỉnh sửa menu"
                        />
                        <Popconfirm
                            title="Bạn có chắc chắn muốn xóa menu này?"
                            onConfirm={() => handleDeleteMenu(record.id!, storeId)}
                            okText="Xóa"
                            cancelText="Hủy"
                        >
                            <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                loading={menuDeleteLoading === record.id}
                                size="small"
                                title="Xóa menu"
                            />
                        </Popconfirm>
                    </Space>
                ),
            },
        ];

        return (
            <Table
                columns={menuColumns}
                dataSource={menus}
                loading={loadingMenus}
                rowKey="id"
                pagination={false}
                size="small"
                locale={{
                    emptyText: 'Chưa có menu nào. Nhấn "Thêm menu" để tạo menu mới.'
                }}
            />
        );
    };

    const columns: TableColumnsType<Store> = [
        {
            title: 'Tên cửa hàng',
            dataIndex: 'name',
            key: 'name',
            width: 200,
            render: (name: string) => <strong>{name}</strong>,
        },
        {
            title: 'Địa chỉ',
            dataIndex: 'address',
            key: 'address',
            width: 250,
            render: (address: string, record: Store) => (
                <div>
                    <div>{address}</div>
                    <small style={{ color: '#8c8c8c' }}>
                        {record.ward}, {record.district}, {record.province}
                    </small>
                </div>
            ),
        },
        {
            title: 'Giờ làm việc',
            dataIndex: 'workingHours',
            key: 'workingHours',
            width: 200,
            render: (workingHours: any) => formatWorkingHours(workingHours),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 120,
            render: (isActive: boolean, record: Store) => (
                <Switch
                    checked={isActive}
                    loading={toggleLoading === record.id}
                    onChange={(checked) => handleToggleStatus(record.id!, checked)}
                    checkedChildren="Hoạt động"
                    unCheckedChildren="Tạm dừng"
                />
            ),
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 200,
            render: (_, record: Store) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record)}
                        title="Xem chi tiết"
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        title="Chỉnh sửa"
                    />
                    <Button
                        type="text"
                        icon={<MenuOutlined />}
                        onClick={() => handleExpandStore(record.id!)}
                        title="Menu cửa hàng"
                    />
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa cửa hàng này?"
                        onConfirm={() => handleDelete(record.id!)}
                        okText="Xóa"
                        cancelText="Hủy"
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            loading={deleteLoading === record.id}
                            title="Xóa"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <AdminLayout>
            <div className="admin-page-header">
                <div className="admin-page-title">
                    <h1 ref={titleRef}>Quản lý cửa hàng</h1>
                    <Space>
                        <Button
                            type="primary"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => setIsTourOpen(true)}
                        >
                            Hướng dẫn
                        </Button>
                        <Button
                            ref={addButtonRef}
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            Thêm cửa hàng
                        </Button>
                    </Space>
                </div>
            </div>

            <Card>
                <div ref={tableRef}>
                    <Table
                        columns={columns}
                        dataSource={stores}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total) => `Tổng số ${total} cửa hàng`,
                        }}
                        scroll={{ x: 1200 }}
                        expandable={{
                            expandedRowKeys: expandedStoreId ? [expandedStoreId] : [],
                            onExpand: (expanded, record) => {
                                if (expanded) {
                                    handleExpandStore(record.id!);
                                } else {
                                    setExpandedStoreId(null);
                                }
                            },
                            expandedRowRender: (record) => (
                                <div style={{ margin: '16px 0' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                        <h3 style={{ margin: 0 }}>Menu của {record.name}</h3>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => handleAddMenu(record.id!, record.name)}
                                        >
                                            Thêm menu
                                        </Button>
                                    </div>
                                    {renderMenuTable(record.id!)}
                                </div>
                            ),
                            expandIcon: ({ expanded, onExpand, record }) => (
                                <Button
                                    type="text"
                                    icon={expanded ? <UpOutlined /> : <DownOutlined />}
                                    onClick={(e) => onExpand(record, e)}
                                    title={expanded ? 'Thu gọn' : 'Xem menu'}
                                />
                            ),
                        }}
                    />
                </div>
            </Card>

            {/* Modal thêm/sửa cửa hàng */}
            <Modal
                title={editingStore ? 'Chỉnh sửa cửa hàng' : 'Thêm cửa hàng mới'}
                visible={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={800}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="name"
                                label="Tên cửa hàng"
                                rules={[{ required: true, message: 'Vui lòng nhập tên cửa hàng' }]}
                            >
                                <Input placeholder="Nhập tên cửa hàng" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="isActive"
                                label="Trạng thái"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="Hoạt động" unCheckedChildren="Tạm dừng" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item
                                name="province"
                                label="Tỉnh/Thành phố"
                                rules={[{ required: true, message: 'Vui lòng nhập tỉnh/thành phố' }]}
                            >
                                <Input placeholder="Ví dụ: Hà Nội" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="district"
                                label="Quận/Huyện"
                                rules={[{ required: true, message: 'Vui lòng nhập quận/huyện' }]}
                            >
                                <Input placeholder="Ví dụ: Quận Hai Bà Trưng" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="ward"
                                label="Phường/Xã"
                                rules={[{ required: true, message: 'Vui lòng nhập phường/xã' }]}
                            >
                                <Input placeholder="Ví dụ: Phường Bách Khoa" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="street"
                        label="Đường/Phố"
                        rules={[{ required: true, message: 'Vui lòng nhập tên đường/phố' }]}
                    >
                        <Input placeholder="Ví dụ: Số 1 Đại Cồ Việt" />
                    </Form.Item>

                    <Form.Item
                        name="address"
                        label="Địa chỉ chi tiết"
                        rules={[{ required: true, message: 'Vui lòng nhập địa chỉ chi tiết' }]}
                    >
                        <TextArea
                            placeholder="Nhập địa chỉ chi tiết (số nhà, tòa nhà, lô, ...)"
                            autoSize={{ minRows: 2, maxRows: 4 }}
                        />
                    </Form.Item>

                    {/* Giờ làm việc */}
                    <div style={{ border: '1px solid #d9d9d9', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                        <h3 style={{ marginBottom: 16 }}>
                            <ClockCircleOutlined /> Giờ làm việc
                        </h3>

                        {/* Trong tuần */}
                        <div style={{ marginBottom: 16 }}>
                            <h4>Thứ 2 - Thứ 6:</h4>
                            <Row gutter={16} align="middle">
                                <Col span={6}>
                                    <Form.Item
                                        name={['workingHours', 'weekdays', 'isOpen']}
                                        valuePropName="checked"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Switch checkedChildren="Mở cửa" unCheckedChildren="Đóng cửa" />
                                    </Form.Item>
                                </Col>
                                <Col span={9}>
                                    <Form.Item
                                        name={['workingHours', 'weekdays', 'open']}
                                        label="Giờ mở cửa"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <TimePicker
                                            format="HH:mm"
                                            placeholder="Chọn giờ mở cửa"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={9}>
                                    <Form.Item
                                        name={['workingHours', 'weekdays', 'close']}
                                        label="Giờ đóng cửa"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <TimePicker
                                            format="HH:mm"
                                            placeholder="Chọn giờ đóng cửa"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>

                        {/* Cuối tuần */}
                        <div>
                            <h4>Thứ 7 - Chủ nhật:</h4>
                            <Row gutter={16} align="middle">
                                <Col span={6}>
                                    <Form.Item
                                        name={['workingHours', 'weekends', 'isOpen']}
                                        valuePropName="checked"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Switch checkedChildren="Mở cửa" unCheckedChildren="Đóng cửa" />
                                    </Form.Item>
                                </Col>
                                <Col span={9}>
                                    <Form.Item
                                        name={['workingHours', 'weekends', 'open']}
                                        label="Giờ mở cửa"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <TimePicker
                                            format="HH:mm"
                                            placeholder="Chọn giờ mở cửa"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={9}>
                                    <Form.Item
                                        name={['workingHours', 'weekends', 'close']}
                                        label="Giờ đóng cửa"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <TimePicker
                                            format="HH:mm"
                                            placeholder="Chọn giờ đóng cửa"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitLoading}
                            >
                                {editingStore ? 'Cập nhật' : 'Thêm mới'}
                            </Button>
                            <Button
                                onClick={() => setModalVisible(false)}
                                disabled={submitLoading}
                            >
                                Hủy
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Modal xem chi tiết */}
            <Modal
                title="Chi tiết cửa hàng"
                visible={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        Đóng
                    </Button>
                ]}
                width={600}
            >
                {viewingStore && (
                    <Descriptions column={1} bordered>
                        <Descriptions.Item label="Tên cửa hàng">
                            <strong>{viewingStore.name}</strong>
                        </Descriptions.Item>
                        <Descriptions.Item label="Trạng thái">
                            <Tag color={viewingStore.isActive ? 'green' : 'red'}>
                                {viewingStore.isActive ? 'Hoạt động' : 'Tạm dừng'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Địa chỉ chi tiết">
                            {viewingStore.address}
                        </Descriptions.Item>
                        <Descriptions.Item label="Phường/Xã">
                            {viewingStore.ward}
                        </Descriptions.Item>
                        <Descriptions.Item label="Quận/Huyện">
                            {viewingStore.district}
                        </Descriptions.Item>
                        <Descriptions.Item label="Tỉnh/Thành phố">
                            {viewingStore.province}
                        </Descriptions.Item>
                        <Descriptions.Item label="Đường/Phố">
                            {viewingStore.street}
                        </Descriptions.Item>
                        <Descriptions.Item label="Giờ làm việc">
                            {formatWorkingHours(viewingStore.workingHours)}
                        </Descriptions.Item>
                        {viewingStore.createdAt && (
                            <Descriptions.Item label="Ngày tạo">
                                {new Date(viewingStore.createdAt).toLocaleString('vi-VN')}
                            </Descriptions.Item>
                        )}
                        {viewingStore.updatedAt && (
                            <Descriptions.Item label="Cập nhật lần cuối">
                                {new Date(viewingStore.updatedAt).toLocaleString('vi-VN')}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            {/* Modal thêm/sửa menu */}
            <Modal
                title={editingMenu ? 'Chỉnh sửa menu' : 'Thêm menu mới'}
                visible={menuModalVisible}
                onCancel={() => {
                    setMenuModalVisible(false);
                    menuForm.resetFields();
                }}
                footer={null}
                width={900}
            >
                <style>{`
                    .auto-height-select .ant-select-selector {
                        min-height: 40px !important;
                        height: auto !important;
                        padding: 8px !important;
                        display: flex !important;
                        flex-wrap: wrap !important;
                        align-items: flex-start !important;
                    }
                    
                    .auto-height-select .ant-select-selection-search {
                        position: static !important;
                        flex: 1 1 auto !important;
                        min-width: 120px !important;
                        margin: 2px !important;
                    }
                    
                    .auto-height-select .ant-select-selection-search-input {
                        height: 28px !important;
                        min-height: 28px !important;
                    }
                    
                    .auto-height-select .ant-select-selection-item {
                        margin: 2px !important;
                        padding: 4px 8px !important;
                        background: #f0f0f0 !important;
                        border: 1px solid #d9d9d9 !important;
                        border-radius: 6px !important;
                        font-size: 14px !important;
                        line-height: 1.4 !important;
                        white-space: nowrap !important;
                        height: auto !important;
                        max-width: calc(100% - 8px) !important;
                        flex: 0 0 auto !important;
                    }
                    
                    .auto-height-select .ant-select-selection-overflow {
                        display: flex !important;
                        flex-wrap: wrap !important;
                        align-items: flex-start !important;
                        width: 100% !important;
                        gap: 0 !important;
                    }
                    
                    .auto-height-select .ant-select-selection-overflow-item {
                        flex: 0 0 auto !important;
                        max-width: none !important;
                    }
                    
                    .auto-height-select .ant-select-selection-overflow-item-suffix {
                        flex: 1 1 auto !important;
                        min-width: 120px !important;
                    }
                    
                    .auto-height-select .ant-select-arrow {
                        top: 8px !important;
                        right: 8px !important;
                    }
                    
                    .auto-height-select .ant-select-clear {
                        top: 8px !important;
                        right: 32px !important;
                    }
                `}</style>
                <Form
                    form={menuForm}
                    layout="vertical"
                    onFinish={handleMenuSubmit}
                >
                    <Row gutter={16}>
                        <Col span={16}>
                            <Form.Item
                                name="menuName"
                                label="Tên menu"
                                rules={[{ required: true, message: 'Vui lòng nhập tên menu' }]}
                            >
                                <Input placeholder="Nhập tên menu" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="isActive"
                                label="Trạng thái"
                                valuePropName="checked"
                            >
                                <Switch checkedChildren="Hoạt động" unCheckedChildren="Tạm dừng" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Chọn món cho menu</Divider>

                    <Form.Item
                        name="selectedProducts"
                        label="Chọn các món muốn thêm vào menu"
                        rules={[{ required: true, message: 'Vui lòng chọn ít nhất một món' }]}
                        style={{
                            marginBottom: '24px'
                        }}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Chọn các món muốn thêm vào menu..."
                            showSearch
                            allowClear
                            filterOption={(input, option) => {
                                const product = allProducts.find(p => p.id === option?.key);
                                if (!product) return false;
                                const searchText = input.toLowerCase();
                                const displayName = (product.originalName || product.name).toLowerCase();
                                return !!(
                                    displayName.includes(searchText) ||
                                    (product.codeCoffee && product.codeCoffee.toLowerCase().includes(searchText)) ||
                                    (product.productCode && product.productCode.toLowerCase().includes(searchText)) ||
                                    (product.code && product.code.toLowerCase().includes(searchText))
                                );
                            }}
                            style={{
                                width: '100%'
                            }}
                            className="auto-height-select"
                            maxTagCount={1000}
                            tagRender={(props) => {
                                const { label, value, closable, onClose } = props;
                                const product = allProducts.find(p => p.id === value);
                                const displayName = product ? (product.originalName || product.name) : label;

                                return (
                                    <div
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            backgroundColor: '#f0f0f0',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '6px',
                                            padding: '4px 8px',
                                            margin: '2px',
                                            fontSize: '14px',
                                            lineHeight: '1.4',
                                            maxWidth: '100%',
                                            wordBreak: 'break-word'
                                        }}
                                    >
                                        <span style={{ marginRight: closable ? '4px' : '0' }}>
                                            {displayName}
                                        </span>
                                        {closable && (
                                            <span
                                                onClick={onClose}
                                                style={{
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    color: '#999',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                ×
                                            </span>
                                        )}
                                    </div>
                                );
                            }}
                            dropdownStyle={{
                                maxHeight: '300px',
                                overflow: 'auto'
                            }}
                        >
                            {allProducts.map(product => (
                                <Select.Option key={product.id} value={product.id}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>{product.originalName || product.name}</span>
                                        <span style={{
                                            fontSize: '12px',
                                            color: '#999',
                                            marginLeft: '8px'
                                        }}>
                                            {product.type === 'coffee' ? 'Hạt cà phê' :
                                                product.type === 'bottledDrink' ? 'Đồ uống đóng chai' : 'Đồ uống'}
                                        </span>
                                    </div>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item style={{ marginTop: 24 }}>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={menuSubmitLoading}
                            >
                                {editingMenu ? 'Cập nhật menu' : 'Tạo menu'}
                            </Button>
                            <Button
                                onClick={() => setMenuModalVisible(false)}
                                disabled={menuSubmitLoading}
                            >
                                Hủy
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Tour component */}
            <ConfigProvider locale={viVN}>
                <Tour
                    open={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    steps={tourSteps}
                    indicatorsRender={(current, total) => (
                        <span>{current + 1} / {total}</span>
                    )}
                    type="primary"
                    arrow={true}
                    placement="bottom"
                    mask={{
                        style: {
                            boxShadow: 'inset 0 0 15px #333',
                        },
                    }}
                />
            </ConfigProvider>
        </AdminLayout>
    );
};

export default StoreManagement; 